import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocation, useSearchParams} from '@github-ui/react-router'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {useNavigate} from '@github-ui/use-navigate'
import {useSafeTimeout} from '@primer/react'
import type {FormEvent, RefObject} from 'react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {TaskRepository} from '../components/repo-branch-agent-selector/RepoBranchAgentSelector'
import {extractChatCommandType} from '../utils/chat-commands-helpers'
import type {
  CopilotChatCommandType,
  CopilotChatMode,
  CopilotChatReference,
  TaskRequestReference,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {copilotLocalStorage} from '../utils/copilot-local-storage'
import {executePotentialSlashCommand} from '../utils/copilot-slash-commands'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {useDefaultModel} from '../utils/models'
import {ChatInputValidationError} from '../utils/validation-error'
import type {AvailableCommand} from './use-available-commands'
import {useLastUsedRepository} from './use-last-used-repository'

const MAX_HEIGHT = 300
const MIN_HEIGHT = 56

interface UseChatInputProps {
  textAreaRef?: RefObject<HTMLTextAreaElement>
  onSubmit?: (text: string, additionalReferences?: CopilotChatReference[]) => Promise<void>
  isLoading?: boolean
  clearPastedFile?: () => void
  onChatCommandChange?: (command: CopilotChatCommandType | null) => void
  disableSavedUserMessage?: boolean
  isInPortal?: boolean
  availableCommands?: AvailableCommand[]
}

interface UseChatInputReturn {
  /** true if the user has provided input since the last message was sent */
  typingHasStarted: boolean
  /** reference to the input textarea */
  textAreaRef: RefObject<HTMLTextAreaElement>
  /** reference to the textarea preview div, which is used to display rich text in the input */
  textareaPreviewRef: RefObject<HTMLDivElement>
  /** container whose height can be adjusted to match the user's input */
  textAreaPreviewContainerRef: RefObject<HTMLDivElement>
  /** the current user input */
  text: string
  /** sets the contents of the input */
  setText: React.Dispatch<React.SetStateAction<string>>
  /** the current chat command extracted from the input, or null if there isn't one */
  command: CopilotChatCommandType | null
  /** sets the current chat command */
  setCommand: React.Dispatch<React.SetStateAction<CopilotChatCommandType | null>>
  /** function to call to send a message */
  handleSubmit: (e?: FormEvent) => Promise<void>
  /** function to call to send a message to a new thread */
  handleSubmitToNewThread: () => Promise<void>
  /** handler for the onScroll event of the input textarea */
  handleScroll: () => void
  /** handler for the onChange event of the input textarea */
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** call to stop response streaming */
  handleStop: () => Promise<void>
  /** handler to move cursor to the end of prompt loaded from query params */
  handleFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  /**
   * agent tasks
   */
  selectedRepo: TaskRepository | undefined
  setSelectedRepo: (repo: TaskRepository | undefined) => void
  selectedBranch: string | undefined
  setSelectedBranch: React.Dispatch<React.SetStateAction<string | undefined>>
  selectedCustomAgent: string | undefined
  setSelectedCustomAgent: React.Dispatch<React.SetStateAction<string | undefined>>
}

/**
 * Does a bunch of stuff, too much really, to manage a chat input for a copilot chat.
 */
export function useChatInput(props: UseChatInputProps): UseChatInputReturn {
  const state = useChatState()
  const manager = useChatManager()
  const navigate = useNavigate()
  const {selectedThreadID, mode, autoSubmit, model} = state
  const {
    clearPastedFile,
    isLoading,
    onSubmit,
    onChatCommandChange,
    disableSavedUserMessage,
    isInPortal,
    availableCommands = [],
  } = props
  const savedUserMessage = copilotLocalStorage.getSavedMessage(selectedThreadID)
  const savedUserMessageOnError = copilotLocalStorage.getSavedUserMessageOnError(selectedThreadID)
  const {pathname} = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [text, setText] = useState(() => {
    // we figure out the initial text here so it will be available in the first render
    const prompt = getPromptFromSearchParams(state.mode, pathname, searchParams)
    if (disableSavedUserMessage) {
      return prompt ?? ''
    } else {
      return prompt ?? savedUserMessageOnError ?? savedUserMessage ?? ''
    }
  })
  const [command, setCommand] = useState<CopilotChatCommandType | null>(null)

  const [typingHasStarted, setTypingHasStarted] = useState(false)
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const textAreaRef = props.textAreaRef || internalRef
  const textareaPreviewRef = useRef<HTMLDivElement>(null)
  const textAreaPreviewContainerRef = useRef<HTMLDivElement>(null)
  const recallIndex = useRef(0)

  const reloadThreadTimerRef = useRef<number | null>(null)
  // Store manager callback, which will be called when state changes
  const reloadThreadFetchMessagesCallbackRef = useRef(() => {})
  const handleAutoSubmitRef = useRef(false)
  const defaultModel = useDefaultModel(state.availableModels)
  const prevThreadID = useRef<string | null>(selectedThreadID)
  const {safeSetTimeout} = useSafeTimeout()
  const prevContainerHeight = useRef(0)

  // creating a task
  const [lastUsedRepo, setLastUsedRepo] = useLastUsedRepository()

  const [selectedRepo, setSelectedRepo] = useState<TaskRepository | undefined>(() => {
    if (lastUsedRepo) {
      const nwo = `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`
      return {
        id: lastUsedRepo.id,
        nameWithOwner: nwo,
        enabled: true,
        name: nwo,
        owner: lastUsedRepo.ownerLogin,
        nameOnly: lastUsedRepo.name,
        defaultBranch: lastUsedRepo.defaultBranch || 'main',
        visibility: lastUsedRepo.visibility,
      }
    }
  })

  const onSelectRepo = useCallback(
    (repo: TaskRepository | undefined) => {
      setSelectedRepo(repo)
      const updateLastUsedRepository = async () => {
        if (repo) {
          const result = await manager.service.fetchRepo(repo.id)
          if (result.ok && result.payload) {
            setLastUsedRepo(result.payload)
          }
        }
      }
      void updateLastUsedRepository()
    },
    [manager.service, setLastUsedRepo],
  )
  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(() => selectedRepo?.defaultBranch)
  const [selectedCustomAgent, setSelectedCustomAgent] = useState<string | undefined>(undefined)

  // Kick this off in the background so it doesn't block the rest of the chat from loading
  const checkForKnowledgeBases = useCallback(async () => {
    // This is used by the popover so if we don't need to render it, don't check for knowledge bases
    if (state.renderAttachKnowledgeBaseHerePopover) {
      await manager.fetchKnowledgeBases()
    }
  }, [manager, state.renderAttachKnowledgeBaseHerePopover])

  useEffect(() => {
    void checkForKnowledgeBases()
  }, [checkForKnowledgeBases])

  useEffect(() => {
    if (!copilotFeatureFlags.stableConversationView) {
      copilotLocalStorage.setSavedMessage(selectedThreadID, text)
      return
    }
    if (selectedThreadID === prevThreadID.current) {
      copilotLocalStorage.setSavedMessage(selectedThreadID, text)
    } else {
      setText(copilotLocalStorage.getSavedMessage(selectedThreadID) || '')
      prevThreadID.current = selectedThreadID
    }
  }, [selectedThreadID, text])

  useLayoutEffect(() => {
    let tries = 0

    function adjustHeight() {
      if (!textAreaRef.current || !textAreaPreviewContainerRef.current || !textareaPreviewRef.current) return

      tries++
      if ((isInPortal || mode === 'assistive') && textAreaRef.current.scrollHeight === 0 && tries < 10) {
        // because this is (often) in a Portal, useLayoutEffect() can run before we're inserted in the DOM, and we are
        // thus unable to get a correct scrollHeight. in that case, let's try again in a bit
        safeSetTimeout(adjustHeight, 1)
        return
      }

      if (text.length === 0 && prevContainerHeight.current === MIN_HEIGHT) {
        return
      }

      textAreaRef.current.style.height = '0'
      const scrollHeight = textAreaRef.current.scrollHeight
      const containerHeight = Math.min(scrollHeight, MAX_HEIGHT)

      textareaPreviewRef.current.style.height = `${scrollHeight}px`
      textAreaRef.current.style.height = `${scrollHeight}px`
      textAreaPreviewContainerRef.current.style.height = `${containerHeight}px`

      prevContainerHeight.current = containerHeight
    }

    adjustHeight()
  }, [mode, safeSetTimeout, text, textAreaRef, textareaPreviewRef, isInPortal])

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // keep the current text focus
    if (e.currentTarget.selectionStart > 0) {
      return
    }
    // else set focus to end of text
    e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
  }

  const handleScroll = useCallback(() => {
    if (!textAreaRef?.current || !textareaPreviewRef?.current || !textAreaPreviewContainerRef?.current) return

    textareaPreviewRef.current.scrollTop = textAreaRef.current.scrollTop
    textareaPreviewRef.current.scrollLeft = textAreaRef.current.scrollLeft
  }, [textAreaRef])

  const sendableReferences = useMemo(
    () => state.currentReferences.filter(ref => !('isClientOnly' in ref) || !ref.isClientOnly),
    [state.currentReferences],
  )

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      setTypingHasStarted(false)

      // Clear all previously saved messages
      copilotLocalStorage.clearSavedUserMessage(selectedThreadID)
      copilotLocalStorage.clearSavedUserMessageOnError(selectedThreadID)

      if (state.defaultRecipient && !text.includes(`@${state.defaultRecipient}`)) {
        manager.clearDefaultRecipient()
      }

      e?.preventDefault() // TODO: fix

      const ranSlashCommand = await executePotentialSlashCommand(
        text,
        state,
        manager,
        navigate,
        availableCommands,
        command === 'task'
          ? {
              selectedRepo,
              selectedBranch,
              selectedCustomAgent,
            }
          : {},
      )
      if (ranSlashCommand) {
        sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_EXECUTED', mode: state.mode, command: text})
        if (text.trim() === '/new') {
          copilotLocalStorage.setSavedMessageFast(selectedThreadID, null)
        }
        setText('')
        return
      }

      if (!isLoading) {
        let messageToSubmit = text
        if (!messageToSubmit) {
          // get references from state
          const refs = sendableReferences
          const [inferred, newMessage] = manager.tryInferMessage(refs)
          if (!inferred || !newMessage) {
            return
          } else {
            messageToSubmit = newMessage
          }
        }
        try {
          setText('')
          const additionalReferences: CopilotChatReference[] = []

          // Build task request reference if this is a task command
          if (command === 'task' && selectedRepo) {
            const taskRequestReference: TaskRequestReference = {
              type: 'task-request',
              description: messageToSubmit,
              repoOwner: selectedRepo.owner,
              repoName: selectedRepo.nameOnly,
              ref: selectedBranch || selectedRepo.defaultBranch,
              customAgent: selectedCustomAgent,
            }
            additionalReferences.push(taskRequestReference)
          }

          await onSubmit?.(messageToSubmit, additionalReferences.length > 0 ? additionalReferences : undefined)
        } catch (error) {
          // If the error is a validation error, preserve the text so the user can see their input
          // The error handling (like showing validation messages) should be handled by the onSubmit callback
          if (error instanceof ChatInputValidationError) {
            // Don't re-throw validation errors to avoid console errors - the onSubmit callback should handle displaying them
            setText(messageToSubmit)
            return
          }
          // Re-throw unexpected errors as they indicate real problems that need to be logged/handled
          throw error
        }
      }
    },
    [
      selectedThreadID,
      state,
      text,
      manager,
      navigate,
      availableCommands,
      command,
      selectedRepo,
      selectedBranch,
      selectedCustomAgent,
      isLoading,
      sendableReferences,
      onSubmit,
    ],
  )

  const handleSubmitToNewThread = useCallback(
    () => manager.sendMessageToNewThread(selectedThreadID, text, sendableReferences, state.context, defaultModel),
    [manager, selectedThreadID, state.context, sendableReferences, text, defaultModel],
  )

  useEffect(() => {
    const handlePrompt = async () => {
      const prompt = getPromptFromSearchParams(mode, pathname, searchParams)
      const updatedSearchParams = new URLSearchParams(searchParams)
      const queryModel = updatedSearchParams.get('model')
      const modelAvailable = !queryModel || queryModel === model.id

      if (prompt && modelAvailable && !handleAutoSubmitRef.current) {
        updatedSearchParams.delete('prompt')
        updatedSearchParams.delete('model')
        sendEvent('dotcom_chat.activate', {target: 'COPILOT_PROMPT_LOADED_FROM_URL', mode: 'immersive'})
        setSearchParams(updatedSearchParams, {replace: true})
        if (autoSubmit) {
          handleAutoSubmitRef.current = true
          // Wait for state update before submitting
          await Promise.resolve()
          await handleSubmit()
        }
      }
    }
    void handlePrompt()
  }, [pathname, searchParams, setSearchParams, mode, autoSubmit, model, handleSubmit])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTypingHasStarted(true)

      if (!textAreaRef?.current) return

      setText((e.target as HTMLTextAreaElement).value)
      clearPastedFile?.()
      recallIndex.current = 0
      handleScroll()
    },
    [handleScroll, clearPastedFile, textAreaRef],
  )

  const handleStop = useCallback(async () => {
    await manager.stopStreaming()

    // Because the request didn't complete, the client only has the local/ephemeral copy of the streamed copilot response,
    // which doesn't have the real message id.
    // Re-fetch the messages from the server so that we have the final state with the correct message ids.
    if (selectedThreadID != null) {
      // Block new messages or retries from being sent while we're reloading the thread
      manager.startThreadReload()
      // Wait 6 seconds for server to complete the saving of the message
      // This matches server-side cancelation logic timing
      await new Promise<string>(timerDone => {
        reloadThreadTimerRef.current = setTimeout(() => timerDone(selectedThreadID), 6 * 1000) as unknown as number
      })
      reloadThreadFetchMessagesCallbackRef.current = await manager.fetchMessages(selectedThreadID, true, true)
    }
  }, [manager, selectedThreadID])

  // Once messages change in state, unblock registered fetchMessages await
  useEffect(() => {
    if (reloadThreadFetchMessagesCallbackRef.current) {
      reloadThreadFetchMessagesCallbackRef.current()
      reloadThreadFetchMessagesCallbackRef.current = () => {}
    }
  }, [state.messages])

  useEffect(() => {
    // Cleanup function to clear the reload timer on unmount or re-render
    return () => {
      // Return cleanup function - when component unmounts, stop the timer so that it doesn't try to reload the old thread
      if (reloadThreadTimerRef.current) {
        clearTimeout(reloadThreadTimerRef.current)
        reloadThreadTimerRef.current = null
      }
      // Also cancel the fetchMessages callbacks
      manager.cancelThreadReload()
      reloadThreadFetchMessagesCallbackRef.current = () => {}
    }
  }, [manager])

  useEffect(() => {
    // Only process chat commands for new conversations unless task within chat thread is enabled
    if (availableCommands.length === 0 && !copilotFeatureFlags.taskWithinChatThread) {
      return
    }

    const newCommand = extractChatCommandType(
      text,
      availableCommands.map(cmd => cmd.key),
    )
    if (command !== newCommand) {
      setCommand(newCommand)
      onChatCommandChange?.(newCommand)
    }
  }, [text, command, onChatCommandChange, availableCommands])

  return {
    typingHasStarted,
    textAreaRef,
    textareaPreviewRef,
    textAreaPreviewContainerRef,
    text,
    setText,
    command,
    setCommand,
    handleSubmit,
    handleSubmitToNewThread,
    handleScroll,
    handleChange,
    handleStop,
    handleFocus,

    // task
    selectedRepo,
    setSelectedRepo: onSelectRepo,
    selectedBranch,
    setSelectedBranch,
    selectedCustomAgent,
    setSelectedCustomAgent,
  }
}

function getPromptFromSearchParams(mode: CopilotChatMode, pathname: string, searchParams: URLSearchParams) {
  if (mode === 'immersive' && /^\/copilot(\/agents|\/spaces\/[a-zA-Z0-9-_]+\/[0-9]+)?\/?$/.test(pathname)) {
    // Load prompt from URL if it exists
    const prompt = searchParams.get('prompt')
    if (prompt) {
      return prompt
    }
  }
  return undefined
}
